import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import type {
	FormChoiceSourceModel,
	FormConsentPurpose,
	FormFieldCustomerAccess,
	FormFieldOptionsModel,
	FormFieldIdentity,
	FormFieldType,
	FormFieldValidationRulesModel,
	FormSelectOptionModel,
	FormShowWhenModel,
} from '../../forms/form-types';
import { BaseEntity } from './base.entity';
import { FormEntity } from './form.entity';

/**
 * The live definition of one field on a form. The same shape as `registration_field`, so
 * the registration field builder carries over, plus what Forms adds (admin label,
 * identity, consent purpose). Every change also writes a new `form_version`, which is
 * what submissions point at.
 *
 * Nothing here is locked because of the kind of form it is (owner, 2026-09-27): what
 * limits a change is the minimum fields the form's active actions need. A field that has
 * ever held data is deactivated rather than deleted, and its key is never reused for a
 * different question.
 */
@Index('idx_form_field_form_active', ['fkFormId', 'isActive'], {})
@Entity('form_field')
export class FormFieldEntity extends BaseEntity {
	@Column('int', { name: 'fk_form__id', unsigned: true })
	public fkFormId!: number;

	// The key the answer is stored and submitted under.
	@Column('varchar', { name: 'fieldKey', length: 60 })
	public fieldKey!: string;

	// What the visitor reads. Long, because a consent label is a full sentence.
	@Column('varchar', { name: 'label', length: 1000 })
	public label!: string;

	// What staff read in the inbox, exports and mappings when the public label is long.
	@Column('varchar', { name: 'adminLabel', nullable: true, length: 120 })
	public adminLabel!: string | null;

	@Column('varchar', { name: 'fieldType', length: 50 })
	public fieldType!: FormFieldType;

	@Column('varchar', { name: 'placeholder', nullable: true, length: 255 })
	public placeholder!: string | null;

	@Column('varchar', { name: 'helpText', nullable: true, length: 500 })
	public helpText!: string | null;

	@Column({ type: 'boolean', name: 'isRequired', default: false })
	public isRequired!: boolean;

	@Column({ type: 'boolean', name: 'isActive', default: true })
	public isActive!: boolean;

	@Column('int', { name: 'sortOrder', default: () => "'0'" })
	public sortOrder!: number;

	@Column('text', { name: 'defaultValue', nullable: true })
	public defaultValue!: string | null;

	@Column('json', { name: 'selectOptions', nullable: true })
	public selectOptions!: Array<FormSelectOptionModel> | null;

	// minLength / maxLength / pattern / min / max, enforced by the shared validator.
	@Column('json', { name: 'validationRules', nullable: true })
	public validationRules!: FormFieldValidationRulesModel | null;

	// What the field says about the submitter (email, phone, company, …).
	@Column('varchar', { name: 'identity', nullable: true, length: 20 })
	public identity!: FormFieldIdentity | null;

	// For consent fields: what the checkbox records agreement to.
	@Column('varchar', { name: 'consentPurpose', nullable: true, length: 20 })
	public consentPurpose!: FormConsentPurpose | null;

	// For a `terms` consent: the version (date the text last changed) of each policy.
	@Column('json', { name: 'policyVersions', nullable: true })
	public policyVersions!: Record<string, string> | null;

	// For a consent field: the terms themselves, shown in a scrolling box under the
	// checkbox. Simple formatting only (bold, italics, lists, links, paragraphs), stored
	// as HTML already reduced by the shared rich-text allow-list, and sanitized again
	// wherever it is shown. Part of the form version, so a submission keeps the terms
	// its submitter saw.
	@Column('text', { name: 'termsBody', nullable: true })
	public termsBody!: string | null;

	// For a consent field: a link to the full document, opened in a new tab. Absolute
	// https addresses only.
	@Column('varchar', { name: 'termsUrl', nullable: true, length: 2048 })
	public termsUrl!: string | null;

	// The wording of that link. Empty shows a standard "Read the full terms".
	@Column('varchar', { name: 'termsLinkText', nullable: true, length: 200 })
	public termsLinkText!: string | null;

	// For an account-manager ("choose a person") question: whose names it lists, as
	// { roles: role ids, users: user ids }. The choices themselves are worked out each time
	// the form is drawn (active users in those roles, plus those people, never Workd
	// staff), so a new hire or a departure changes the list without editing the form.
	@Column('json', { name: 'choiceSource', nullable: true })
	public choiceSource!: FormChoiceSourceModel | null;

	// PD-16730 — show this question only when an earlier dropdown, multiple-choice or
	// yes/no question has one of the chosen answers: { fieldKey, values }. Null always
	// shows it. A hidden question is never required and its answer is not kept, so the
	// server, not the page, decides what a submission holds (form-show-when.ts).
	@Column('json', { name: 'showWhen', nullable: true })
	public showWhen!: FormShowWhenModel | null;

	// PD-13768 — sent forms (sent-forms.ts). Where the value comes from when the form is
	// sent to a client: a client, contact or address field from the source catalog
	// (`client.name`, `shipping.city`, `contact.email`, `customField:<key>` …), `staff`
	// (typed by the sender before sending), or null (nothing; the customer fills it).
	@Column('varchar', { name: 'valueSource', nullable: true, length: 120 })
	public valueSource!: string | null;

	// PD-13768 — what the customer may do with it on a sent form: fill it, change the
	// prefilled value, only read it (locked), or not see it at all (internal). Null reads
	// as `fill`. A staff-filled field is always locked, whatever this says.
	@Column('varchar', { name: 'customerAccess', nullable: true, length: 20 })
	public customerAccess!: FormFieldCustomerAccess | null;

	// PD-13768 — the client or contact field a customer's change is proposed for, accepted
	// or rejected by staff when the submission is processed. Never written silently.
	@Column('varchar', { name: 'writeBackTarget', nullable: true, length: 120 })
	public writeBackTarget!: string | null;

	// PD-13768 — type-specific settings: a table's columns and row source, a payment
	// method question's kinds and Bill To choice, a signature's draw / type modes, where a
	// terms text opens, and choices worked out by the server (term lengths, billing terms).
	@Column('json', { name: 'fieldOptions', nullable: true })
	public fieldOptions!: FormFieldOptionsModel | null;

	@Column({
		type: 'boolean',
		name: 'includeInNotification',
		default: true,
	})
	public includeInNotification!: boolean;

	@ManyToOne(() => FormEntity, { createForeignKeyConstraints: false })
	@JoinColumn([{ name: 'fk_form__id', referencedColumnName: 'id' }])
	public form!: FormEntity;
}
